import { generateClient } from 'aws-amplify/data';
import logger from '../utils/logger.js';
import { getCurrentUser } from 'aws-amplify/auth';
import {
  createNetworkError,
  createValidationError,
  isErrorResponse,
  logError
} from '../utils/StandardErrorFormat.js';

/**
 * Sprint 6 M3 (#151 P0 #1) — daily spend cap.
 *
 * Thrown by `assertWithinDailyBudget(userId)` when today's accumulated
 * AI cost for `userId` is over the per-user daily budget. UI catches
 * this and surfaces a friendly "out of budget for today" message rather
 * than a 500. Caller code can also branch on `err.name === 'QuotaExceededError'`.
 */
class QuotaExceededError extends Error {
  constructor(message, { userId, dailyCost, dailyBudget } = {}) {
    super(message);
    this.name = 'QuotaExceededError';
    this.userId = userId;
    this.dailyCost = dailyCost;
    this.dailyBudget = dailyBudget;
  }
}

const DEFAULT_DAILY_BUDGET_USD = 5.0;

const todayKey = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};

const dailyBudgetUsd = () => {
  // Read at call time so tests + per-env config can override.
  const raw = (typeof import.meta !== 'undefined' && import.meta.env?.VITE_AI_DAILY_BUDGET_USD)
    || (typeof process !== 'undefined' && process.env?.VITE_AI_DAILY_BUDGET_USD);
  const parsed = parseFloat(raw);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : DEFAULT_DAILY_BUDGET_USD;
};

/**
 * AIUsageStatsService - Manages AI usage statistics via GraphQL
 *
 * Replaces localStorage-based tracking with backend persistence
 * Tracks token usage, costs, and request counts with monthly breakdowns
 */
class AIUsageStatsService {
  constructor() {
    // Create service-specific logger
    this.logger = logger.child('AIUsageStatsService');

    this.client = generateClient();
    this.cache = null;
    this.cacheTimestamp = null;
    this.cacheDuration = 60000; // 1 minute cache

    // Sprint 6 M3 (#151 P0 #1) — in-memory per-user-per-day cost
    // tracker. Map keyed by `${userId}::${YYYY-MM-DD}`. Lost on browser
    // refresh; that's OK because the Lambda-side caps (P0 #2 and P0 #3)
    // are the authoritative ceiling. This tracker is the friendly
    // pre-flight check that keeps the UI from racking up runaway calls.
    this._dailyCost = new Map();
  }

  /**
   * Sprint 6 M3 #151 P0 #1 — increment today's running cost for a user.
   * Called by `updateStats` after a successful Bedrock round-trip and
   * exposed publicly so tests + future internal call sites can record
   * cost without going through the full GraphQL update path.
   *
   * @param {string} userId
   * @param {number} costUsd  cost of this single AI call, in USD
   */
  recordDailyCost(userId, costUsd) {
    if (!userId || !Number.isFinite(costUsd)) return;
    const key = `${userId}::${todayKey()}`;
    const prev = this._dailyCost.get(key) || 0;
    this._dailyCost.set(key, prev + costUsd);
  }

  /**
   * Returns today's accumulated AI cost for `userId`. 0 if nothing
   * recorded yet today.
   *
   * @param {string} userId
   * @returns {number} USD spent today
   */
  getDailyCost(userId) {
    if (!userId) return 0;
    const key = `${userId}::${todayKey()}`;
    return this._dailyCost.get(key) || 0;
  }

  /**
   * Sprint 6 M3 #151 P0 #1 — throw QuotaExceededError if today's spend
   * for `userId` is strictly over the per-user budget. Call from the
   * top of every public AI method before the network round-trip.
   *
   * Uses ">" semantics (not ">=") so a $0 cost slip-through doesn't
   * lock the user out at exactly the budget line.
   *
   * @param {string} userId
   * @throws {QuotaExceededError}
   */
  async assertWithinDailyBudget(userId) {
    const dailyCost = this.getDailyCost(userId);
    const budget = dailyBudgetUsd();
    if (dailyCost > budget) {
      throw new QuotaExceededError(
        `Daily AI budget exceeded for user (spent $${dailyCost.toFixed(2)} of $${budget.toFixed(2)} today). Try again tomorrow or contact your administrator.`,
        { userId, dailyCost, dailyBudget: budget }
      );
    }
  }

  /**
   * Get current user ID
   * @returns {Promise<string>} User ID
   */
  async getCurrentUserId() {
    try {
      const user = await getCurrentUser();
      return user.userId;
    } catch (error) {
      this.logger.error('Error getting current user:', error);
      return createValidationError('User not authenticated', error);
    }
  }

  /**
   * Get current month in YYYY-MM format
   * @returns {string} Current month
   */
  getCurrentMonth() {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  }

  /**
   * Get or create AI usage stats for current user
   * @returns {Promise<Object>} AI usage stats
   */
  async getStats() {
    try {
      // Check cache first
      if (this.cache && this.cacheTimestamp && (Date.now() - this.cacheTimestamp < this.cacheDuration)) {
        return this.cache;
      }

      // Check if client and models are available
      if (!this.client || !this.client.models || !this.client.models.AIUsageStats) {
        this.logger.warn('GraphQL client or AIUsageStats model not available, returning default stats');
        return {
          totalTokensUsed: 0,
          totalCost: 0,
          requestCount: 0,
          placeholderGenerationTokens: 0,
          newsletterGenerationTokens: 0,
          styleAnalysisTokens: 0,
          currentMonth: this.getCurrentMonth(),
          monthlyTokensUsed: 0,
          monthlyCost: 0,
          monthlyHistory: '[]',
          lastUpdated: new Date().toISOString()
        };
      }

      const userId = await this.getCurrentUserId();
      const currentMonth = this.getCurrentMonth();

      // Try to get existing stats
      const { data: existingStats } = await this.client.models.AIUsageStats.list({
        filter: {
          userId: { eq: userId }
        }
      });

      let stats;
      if (existingStats && existingStats.length > 0) {
        stats = existingStats[0];

        // Check if we need to roll over to a new month
        if (stats.currentMonth !== currentMonth) {
          // Archive current month to history
          const monthlyHistory = stats.monthlyHistory ? JSON.parse(stats.monthlyHistory) : [];
          monthlyHistory.push({
            month: stats.currentMonth,
            tokens: stats.monthlyTokensUsed || 0,
            cost: stats.monthlyCost || 0,
            requests: stats.requestCount || 0
          });

          // Reset monthly counters
          const { data: updated } = await this.client.models.AIUsageStats.update({
            id: stats.id,
            currentMonth: currentMonth,
            monthlyTokensUsed: 0,
            monthlyCost: 0,
            monthlyHistory: JSON.stringify(monthlyHistory),
            lastUpdated: new Date().toISOString()
          });

          stats = updated;
        }
      } else {
        // Create new stats record
        const { data: created } = await this.client.models.AIUsageStats.create({
          userId: userId,
          totalTokensUsed: 0,
          totalCost: 0,
          requestCount: 0,
          placeholderGenerationTokens: 0,
          newsletterGenerationTokens: 0,
          styleAnalysisTokens: 0,
          currentMonth: currentMonth,
          monthlyTokensUsed: 0,
          monthlyCost: 0,
          monthlyHistory: JSON.stringify([]),
          lastUpdated: new Date().toISOString(),
          createdAt: new Date().toISOString()
        });

        stats = created;
      }

      // Cache the result
      this.cache = stats;
      this.cacheTimestamp = Date.now();

      return stats;
    } catch (error) {
      this.logger.error('Error getting AI usage stats:', error);

      // Return default stats on error
      return {
        totalTokensUsed: 0,
        totalCost: 0,
        requestCount: 0,
        placeholderGenerationTokens: 0,
        newsletterGenerationTokens: 0,
        styleAnalysisTokens: 0,
        currentMonth: this.getCurrentMonth(),
        monthlyTokensUsed: 0,
        monthlyCost: 0,
        monthlyHistory: '[]',
        lastUpdated: new Date().toISOString()
      };
    }
  }

  /**
   * Update AI usage statistics
   * @param {Object} usage - Usage data
   * @param {number} usage.tokensUsed - Tokens used in this request
   * @param {number} usage.cost - Cost of this request
   * @param {string} usage.feature - Feature that used AI (placeholder|newsletter|style)
   * @returns {Promise<Object>} Updated stats
   */
  async updateStats(usage) {
    try {
      const stats = await this.getStats();

      // Calculate new totals (round costs to 4 decimal places to avoid floating point errors while preserving precision)
      const newTotalTokens = (stats.totalTokensUsed || 0) + (usage.tokensUsed || 0);
      const newTotalCost = Math.round(((stats.totalCost || 0) + (usage.cost || 0)) * 10000) / 10000;
      const newRequestCount = (stats.requestCount || 0) + 1;
      const newMonthlyTokens = (stats.monthlyTokensUsed || 0) + (usage.tokensUsed || 0);
      const newMonthlyCost = Math.round(((stats.monthlyCost || 0) + (usage.cost || 0)) * 10000) / 10000;

      // Update feature-specific tokens
      const updates = {
        id: stats.id,
        totalTokensUsed: newTotalTokens,
        totalCost: newTotalCost,
        requestCount: newRequestCount,
        monthlyTokensUsed: newMonthlyTokens,
        monthlyCost: newMonthlyCost,
        lastUpdated: new Date().toISOString()
      };

      // Add feature-specific tracking
      if (usage.feature === 'placeholder') {
        updates.placeholderGenerationTokens = (stats.placeholderGenerationTokens || 0) + (usage.tokensUsed || 0);
      } else if (usage.feature === 'newsletter') {
        updates.newsletterGenerationTokens = (stats.newsletterGenerationTokens || 0) + (usage.tokensUsed || 0);
      } else if (usage.feature === 'style') {
        updates.styleAnalysisTokens = (stats.styleAnalysisTokens || 0) + (usage.tokensUsed || 0);
      }

      // Per-provider cost tracking (Issue #36 AC4)
      if (usage.provider) {
        const providerStats = stats.providerBreakdown ? JSON.parse(stats.providerBreakdown) : {};
        const providerKey = usage.provider;
        if (!providerStats[providerKey]) {
          providerStats[providerKey] = { tokens: 0, cost: 0, requests: 0 };
        }
        providerStats[providerKey].tokens += (usage.tokensUsed || 0);
        providerStats[providerKey].cost = Math.round((providerStats[providerKey].cost + (usage.cost || 0)) * 10000) / 10000;
        providerStats[providerKey].requests += 1;
        updates.providerBreakdown = JSON.stringify(providerStats);
      }

      const { data: updated } = await this.client.models.AIUsageStats.update(updates);

      // Sprint 6 M3 (#151 P0 #1) — increment in-memory daily tracker so
      // the next pre-flight `assertWithinDailyBudget` sees the updated
      // running total. We pull userId from the cached stats record (set
      // earlier in this method's flow via getStats()).
      if (stats?.userId && Number.isFinite(usage?.cost)) {
        this.recordDailyCost(stats.userId, usage.cost);
      }

      // Clear cache to force refresh
      this.cache = null;
      this.cacheTimestamp = null;

      this.logger.info('AI usage stats updated:', {
        tokensUsed: usage.tokensUsed,
        cost: usage.cost,
        feature: usage.feature,
        newTotalTokens,
        newTotalCost
      });

      return updated;
    } catch (error) {
      this.logger.error('Error updating AI usage stats:', error);
      return createNetworkError('Failed to update AI usage statistics', error);
    }
  }

  /**
   * Get formatted stats for display
   * @returns {Promise<Object>} Formatted stats
   */
  async getFormattedStats() {
    try {
      const stats = await this.getStats();

      // Calculate average tokens per request
      const averageTokens = stats.requestCount > 0
        ? Math.round(stats.totalTokensUsed / stats.requestCount)
        : 0;

      return {
        totalGenerations: stats.requestCount || 0,
        tokensUsed: stats.totalTokensUsed || 0,
        totalCost: stats.totalCost || 0,
        averageTokens: averageTokens,
        lastGeneration: stats.lastUpdated || null,
        monthlyTokens: stats.monthlyTokensUsed || 0,
        monthlyCost: stats.monthlyCost || 0,
        placeholderTokens: stats.placeholderGenerationTokens || 0,
        newsletterTokens: stats.newsletterGenerationTokens || 0,
        styleTokens: stats.styleAnalysisTokens || 0
      };
    } catch (error) {
      this.logger.error('Error getting formatted stats:', error);
      return {
        totalGenerations: 0,
        tokensUsed: 0,
        totalCost: 0,
        averageTokens: 0,
        lastGeneration: null,
        monthlyTokens: 0,
        monthlyCost: 0,
        placeholderTokens: 0,
        newsletterTokens: 0,
        styleTokens: 0
      };
    }
  }

  /**
   * Get monthly history
   * @returns {Promise<Array>} Monthly history
   */
  async getMonthlyHistory() {
    try {
      const stats = await this.getStats();
      return stats.monthlyHistory ? JSON.parse(stats.monthlyHistory) : [];
    } catch (error) {
      this.logger.error('Error getting monthly history:', error);
      return [];
    }
  }

  /**
   * Clear cache (useful for testing or forcing refresh)
   */
  clearCache() {
    this.cache = null;
    this.cacheTimestamp = null;
  }
}

// Create singleton instance
const aiUsageStatsService = new AIUsageStatsService();

// Export both the class and the singleton instance
export { AIUsageStatsService, QuotaExceededError };
export default aiUsageStatsService;
